import React, { memo, useEffect, useMemo, useState } from "react";
import { Route, Switch, Redirect, useRouteMatch } from "react-router-dom";
import useAuthorized, {
  SUPPRESSION_CREATE_PERMISSION,
  SUPPRESSION_LIST_PERMISSION,
} from "../../hooks/useAuthorized";
import ListSuppresionList from "./List";
import CreateSuppressionNumber from "./Create";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { clearSuppressions } from "../../actions/suppression";
import {
  defaultChannelType,
  getCallerIds,
  getEmailDomains,
  getMissingConfigMessage,
  getShortCodes,
  normalizeSuppressionType,
} from "./util";
import { setParams } from "../../actions/params";

function SuppressionPage() {
  const match = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();
  const queryParams = useSelector((state) => state.params);
  const canList = useAuthorized(SUPPRESSION_LIST_PERMISSION);
  const canCreatePermission = useAuthorized(SUPPRESSION_CREATE_PERMISSION);
  const [suppressionTab, setSuppressionTab] = useState(
    () => normalizeSuppressionType(queryParams?.type) || defaultChannelType
  );
  const [isTabDefined, setIsTypeDefined] = useState(true);
  const eonsConfig = useSelector((state) => state.eonsConfig) || {};
  const { smsConfig, emailConfig, vendorConfig, voiceConfig } = eonsConfig;

  useEffect(() => {
    dispatch(clearSuppressions());
    const typeNormalized = normalizeSuppressionType(queryParams?.type);
    if (!typeNormalized) {
      history.push(`/suppression?type=${defaultChannelType}`);
      dispatch(setParams({ type: defaultChannelType }));
      setSuppressionTab(defaultChannelType);
      setIsTypeDefined(true);
      return;
    }
    setSuppressionTab(typeNormalized);
    if (queryParams.type !== typeNormalized) {
      dispatch(setParams({ type: typeNormalized }));
    }
    setIsTypeDefined(true);
  }, [dispatch, history, queryParams]);

  const shortCodes = useMemo(() => getShortCodes(smsConfig), [smsConfig]);
  const domainAddresses = useMemo(() => getEmailDomains(emailConfig), [
    emailConfig,
  ]);
  const callerIds = useMemo(() => getCallerIds(vendorConfig, voiceConfig), [
    vendorConfig,
    voiceConfig,
  ]);

  const missingConfigMessage = getMissingConfigMessage(suppressionTab, {
    shortCodes,
    domainAddresses,
    callerIds,
  });
  const canCreate = canCreatePermission && !missingConfigMessage;

  return (
    <>
      <Switch>
        <Route path={`${match.path}/create`}>
          {canCreate ? (
            <CreateSuppressionNumber
              type={suppressionTab}
              domainAddresses={domainAddresses}
              shortCodes={shortCodes}
              callerIds={callerIds}
            />
          ) : isTabDefined ? (
            <Redirect
              to={
                suppressionTab
                  ? `${match.path}?type=${suppressionTab}`
                  : `${match.path}`
              }
            />
          ) : null}
        </Route>
        {(canList || canCreatePermission) && (
          <Route path={`${match.path}`}>
            <ListSuppresionList
              type={suppressionTab}
              domainAddresses={domainAddresses}
              shortCodes={shortCodes}
              callerIds={callerIds}
              canCreate={canCreate}
              canList={canList}
              missingConfigMessage={missingConfigMessage}
            />
          </Route>
        )}
        <Redirect to={`${match.path}`} />
      </Switch>
    </>
  );
}

export default memo(SuppressionPage);
